import type { ReactNode } from 'react';
import { Card, Row } from 'react-bootstrap';
import type { TeamMatchPlayResults } from '../types/api.types';
import { TEAM_COLORS, getTeamColor } from '../constants/teamColors';
import { TeamFlag } from './team-flag';
import { getWinningPlayer, getShotsText } from './head-to-head.utils';

interface MatchResultCardProps {
  result: TeamMatchPlayResults;
  selectedMemberId: string | number;
  selectedMemberName: string;
  currentOpponentId: string | number;
  currentOpponentName: string;
  index: number;
}

const bodyStyle = { padding: 0 };

const getResultScore = (result: TeamMatchPlayResults): ReactNode => {
  if (result.winningTeam === 'AS') {
    return <span style={{ fontSize: '1.3em', fontWeight: 'bold' }}>TIED</span>;
  } else if (result.holesRemaining === 0) {
    return (
      <span>
        <span style={{ fontSize: '1.5em', fontWeight: 'bold' }}>{result.winningMargin}</span>
        <span> up</span>
      </span>
    );
  } else {
    return (
      <span>
        <span style={{ fontSize: '1.5em', fontWeight: 'bold' }}>{result.winningMargin}</span>
        <span> & {result.holesRemaining}</span>
      </span>
    );
  }
};

const getColumnStyle = (
  playerId: string | number,
  team: string | undefined,
  result: TeamMatchPlayResults,
  winningMember: string | number | null
) => {
  if (result.winningTeam === 'AS') {
    return {
      backgroundColor: TEAM_COLORS.TIED.primary,
      padding: '8px',
      borderRadius: '4px',
      fontWeight: 'bold' as const,
    };
  }
  if (winningMember !== playerId) {
    return { padding: '8px' };
  }
  return {
    backgroundColor: getTeamColor(team),
    color: '#ffffff',
    padding: '8px',
    borderRadius: '4px',
    fontWeight: 'bold' as const,
  };
};

export const MatchResultCard = ({
  result,
  selectedMemberId,
  selectedMemberName,
  currentOpponentId,
  currentOpponentName,
  index,
}: MatchResultCardProps) => {
  const winningMember = getWinningPlayer(result);

  const player1Flag = (
    <TeamFlag team={(result.teamAMember1 === selectedMemberId ? result.teamA : result.teamB) as 'USA' | 'EU' | 'EUR'} />
  );
  const player2Flag = (
    <TeamFlag
      team={(result.teamAMember1 === currentOpponentId ? result.teamA : result.teamB) as 'USA' | 'EU' | 'EUR'}
    />
  );

  const player1Team = result.teamAMember1 === selectedMemberId ? result.teamA : result.teamB;
  const player2Team = result.teamAMember1 === currentOpponentId ? result.teamA : result.teamB;

  // Get member names for shots text - we'll need to pass these from parent or get them here
  const teamAMemberName = result.teamAMember1 === selectedMemberId ? selectedMemberName : currentOpponentName;
  const teamBMemberName = result.teamBMember1 === selectedMemberId ? selectedMemberName : currentOpponentName;
  const shotsText = getShotsText(result, teamAMemberName, teamBMemberName);

  return (
    <Row key={`${result.eventID}-${selectedMemberId}-${currentOpponentId}-${index}`}>
      <Card
        style={{
          border: '1px solid #ccc',
          marginBottom: '8px',
        }}
      >
        <Card.Body style={bodyStyle}>
          <div style={{ display: 'flex', gap: '8px', marginBottom: '4px' }}>
            <div
              style={{
                flex: 1,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'flex-end',
                ...getColumnStyle(selectedMemberId, player1Team, result, winningMember),
              }}
            >
              <span>{selectedMemberName}</span>
            </div>
            <div style={{ flexShrink: 0, display: 'flex', alignItems: 'center' }}>{player1Flag}</div>
            <div
              style={{
                flex: '0 0 auto',
                textAlign: 'center',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                minWidth: '100px',
              }}
            >
              <div style={{ fontWeight: 'normal' }}>{result.eventID ? result.eventID.substring(0, 4) : ''}</div>
              <div>{getResultScore(result)}</div>
            </div>
            <div style={{ flexShrink: 0, display: 'flex', alignItems: 'center' }}>{player2Flag}</div>
            <div
              style={{
                flex: 1,
                display: 'flex',
                alignItems: 'center',
                ...getColumnStyle(currentOpponentId, player2Team, result, winningMember),
              }}
            >
              <span>{currentOpponentName}</span>
            </div>
          </div>
          <div style={{ fontSize: '0.9em', color: '#666', textAlign: 'center' }}>{shotsText}</div>
        </Card.Body>
      </Card>
    </Row>
  );
};
